import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, List } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const LIST_TYPES = ['screen', 'board', 'control', 'button', 'topper', 'wiring', 'component', 'cabinet_type'];

const Lists = () => {
  const { t, API, canEdit, isAdmin } = useApp();
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('screen');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    list_type: 'screen',
    value: '',
  });

  useEffect(() => {
    fetchAllLists();
  }, []);

  const fetchAllLists = async () => {
    try {
      const results = await Promise.all(LIST_TYPES.map((type) => axios.get(`${API}/lists/${type}`)));
      const listsData = {};
      LIST_TYPES.forEach((type, index) => {
        listsData[type] = results[index].data;
      });
      setLists(listsData);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.value.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }

    try {
      await axios.post(`${API}/lists`, formData);
      toast.success(t('common.success'));
      setShowCreateDialog(false);
      setFormData({ list_type: activeTab, value: '' });
      fetchAllLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      await axios.delete(`${API}/lists/${selectedItem.id}`);
      toast.success(t('common.success'));
      setShowDeleteDialog(false);
      setSelectedItem(null);
      fetchAllLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const openCreateDialog = () => {
    setFormData({ list_type: activeTab, value: '' });
    setShowCreateDialog(true);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="lists-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('lists.title')}
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-xs">
            {t('lists.title')}
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreateDialog} className="font-display uppercase tracking-[0.14em] glow-amber" data-testid="add-list-item-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('lists.addItem')}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2">
          {LIST_TYPES.map((type) => (
            <TabsTrigger key={type} value={type} className="uppercase font-mono text-xs tracking-wider" data-testid={`tab-${type}`}>
              {t(`lists.${type}`)} <span className="tabular-nums">({lists[type]?.length || 0})</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {LIST_TYPES.map((type) => (
          <TabsContent key={type} value={type} className="mt-6">
            <Card className="bezel">
              <CardHeader>
                <CardTitle className="eyebrow text-[10px] text-muted-foreground">
                  {t(`lists.${type}`)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {lists[type]?.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {lists[type].map((item) => (
                      <div
                        key={item.id}
                        className="group relative flex items-center gap-2 p-3 rounded-sm border border-border bg-muted/40 font-mono hover:bg-muted transition-colors"
                        data-testid={`list-item-${item.id}`}
                      >
                        <span className="font-medium">{item.value}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          ({item.created_by})
                        </span>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                            onClick={() => { setSelectedItem(item); setShowDeleteDialog(true); }}
                            data-testid={`delete-list-item-${item.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <List className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">{t('common.noResults')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-list-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('lists.addItem')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('lists.listType')} *</Label>
              <Select value={formData.list_type} onValueChange={(v) => setFormData({ ...formData, list_type: v })}>
                <SelectTrigger data-testid="list-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIST_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{t(`lists.${type}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('lists.value')} *</Label>
              <Input
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder={t('lists.value')}
                data-testid="list-value-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="create-list-item-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('lists.deleteItem')}</AlertDialogTitle>
            <AlertDialogDescription>{t('cabinets.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-list-item">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Lists;
